import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/cn';

/*
 * Button - perustoimintopainike CVA-varianteilla.
 *
 * Variantit:
 *  - default: kultainen primary-painike (Avustajan AI-toiminnot, julkaisut, vahvistukset).
 *    Kultainen vari (--color-kulta) on SEED 13.7:n mukaan SUOJATTU AI-toiminnoille,
 *    joten taman variantin paalle EI saa ohjata yleisia neutraaleja toimintoja
 *    (esim. "Tallenna asetukset"). Niille kaytetaan secondary tai outline.
 *  - secondary: sammal-tausta valkoisella tekstilla. Ei-AI-paatoksellinen toiminto.
 *  - outline: lapinakyva tausta + sammal-rajaus. Toissijainen toimintovaihtoehto.
 *  - ghost: ilman taustaa, hover-tilassa kevyt tausta. Toolbar-tyyliset toiminnot.
 *  - destructive: tumma punainen poistoja varten.
 *
 * Koko:
 *  - sm/default/lg
 *  - icon (neliohko, vain ikoni-painikkeille)
 *
 * Saavutettavuus: type="button" oletuksena (estaa lomakkeen accidentin submitin),
 * focus-rengas tulee globals.css:n .focus-ring-luokasta jonka data-attribuutti
 * paastaa lapi.
 */

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 rounded-sm font-medium transition-colors focus-ring disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default: 'bg-kulta text-metsa hover:bg-kulta-l',
        secondary: 'bg-sammal text-white hover:bg-sammal-d',
        outline:
          'border border-sammal bg-transparent text-sammal hover:bg-ruoho hover:text-metsa',
        ghost: 'bg-transparent text-teksti hover:bg-ruoho',
        destructive: 'bg-oranssi text-white hover:bg-tuohi-dd',
      },
      size: {
        sm: 'h-8 px-3 text-sm',
        default: 'h-9 px-4 text-base',
        lg: 'h-10 px-6 text-md',
        icon: 'h-9 w-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants>;

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant, size, type = 'button', ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cn(buttonVariants({ variant, size }), className)}
      {...props}
    />
  );
});

// eslint-disable-next-line react-refresh/only-export-components
export { buttonVariants };
export type { ButtonProps };
